import { WithAuth } from "@/hoc";
import { getTenantConfig } from "@/config/tenantConfig";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { copyTextToClipboard } from "@/utils/copyToClipboard";

import { Flex, Box, Text } from "@chakra-ui/react";
import { Api } from "@/services";
import { CheckoutProps } from "@/models";
import { handleApiError, showToast } from "@/utils";
import { Button, Card, Input, PageHeading } from "barte-design-system";
type FormValues = {
  title: string;
  description: string;
  notes: string;
  subDomain: string;
};
const schema = yup.object().shape({
  title: yup.string().required("Título é um campo obrigatório!"),
  subDomain: yup.string().required("Url é um campo obrigatório!"),
});

const MeuCheckoutInfo = () => {
  const [checkoutDefault, setCheckoutDefault] = useState({} as CheckoutProps);
  const [isAddMode, setIsAddMode] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values: any) => {
    if (!Object.keys(values).length || isSaving) return;
    setIsSaving(true);
    try {
      await (isAddMode ? createCheckout(values) : editCheckout(values));
    } finally {
      setIsSaving(false);
    }
  };

  async function getInitialData() {
    const response = await Api.get(`/checkouts/default`);
    setCheckoutDefault(response.data);
    setValue("title", response.data.title);
    setValue("description", response.data.description);
    setValue("subDomain", response.data.subDomain);
  }
  useEffect(() => {
    getInitialData();
  }, []);

  async function createCheckout(data) {
    try {
      const urlCheckout = await Api.get(`subdomains/${data.subDomain}/exists`);
      if (urlCheckout.data.isValid) {
        showToast({
          message: "URL já em uso por outro checkout!",
          type: "error",
        });
      } else {
        try {
          const checkout = await Api.post("/checkouts", data);
          showToast({
            message: "Checkout cadastrado com sucesso!",
            type: "success",
          });        
        } catch (error) {
          handleApiError(error, "Não foi possível cadastrar o checkout.");
        }
        reset({ title: "", description: "", subDomain: "" });
      }
    } catch (error) {
      handleApiError(error, "Não foi possível validar a URL do checkout.");
    }
  }

  async function editCheckout(data: any) {
    try {
      const editCheckout = await Api.put(
        `checkouts/${checkoutDefault.id}`,
        data
      );
      showToast({
        message: "Checkout editado com sucesso!",
        type: "success",
      });
    } catch (error) {
      handleApiError(error, "Não foi possível editar o checkout.");
    }
  }

  useEffect(() => {
    if (!isAddMode) {
      const fields = ["description", "title", "notes", "subDomain"];
      fields.forEach((field: any) => setValue(field, checkoutDefault[field]));
    }
    setValue("subDomain", checkoutDefault.subDomain);
  }, []);

  return (
    <Box>
      <PageHeading
        title="Meu checkout"
        primaryActionLabel={isSaving ? "Salvando..." : "Salvar"}
        onPrimaryAction={handleSubmit(onSubmit)}
      />
      <Box mt="24px">
        <Card>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex direction="column" gap="6">
              <Input
                type="text"
                label="Título do checkout"
                placeholder="Insira o título"
                error={errors?.title?.message}
                defaultValue={checkoutDefault.title}
                {...register("title")}
              />

              <Input
                type="textarea"
                label="Descrição do checkout"
                placeholder="Descrição"
                rows={4}
                error={errors?.description?.message}
                defaultValue={checkoutDefault.description}
                {...register("description")}
              />

              <Box>
                <Text
                  mb="2"
                  fontSize="14px"
                  fontWeight="500"
                  color="var(--content-primary)"
                >
                  URL do checkout
                </Text>
                <Flex align="center" gap="2">
                  <Flex
                    align="center"
                    px="3"
                    h="40px"
                    bg="gray.25"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    fontSize="14px"
                    color="var(--content-tertiary)"
                    whiteSpace="nowrap"
                  >
                    {getTenantConfig().checkoutUrlV2}
                  </Flex>
                  <Box flex="1">
                    <Input
                      type="text"
                      error={errors?.subDomain?.message}
                      defaultValue={checkoutDefault.subDomain}
                      {...register("subDomain")}
                    />
                  </Box>
                  <Button
                    id="checkout"
                    variant="outline"
                    onClick={() =>
                      copyTextToClipboard(
                        `${getTenantConfig().checkoutUrlV2}${getValues("subDomain")}`
                      )
                    }
                  >
                    Copiar link
                  </Button>
                </Flex>
              </Box>
            </Flex>
          </form>
        </Card>
      </Box>
    </Box>
  );
};

export default WithAuth(MeuCheckoutInfo);
